import React from "react";
import { Link } from "react-router-dom";
import useScrollReveal from "../hooks/useScrollReveal";
import Icon from "../components/Icon";

export default function TafseerPage() {
  useScrollReveal();
  return (
    <>
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-20 md:pb-32 bg-surface-bright">
        <div className="absolute inset-0 geometric-pattern-emerald opacity-40"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-fixed opacity-20 blur-3xl rounded-full"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-tertiary-fixed opacity-20 blur-3xl rounded-full"></div>
        <div className="relative page-x max-w-max-width mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
          <div className="flex-1 space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-primary-container text-on-primary-container font-label-md text-label-md">
              <Icon name="stars" className="material-symbols-outlined text-sm" />
              Deep Understanding
            </div>
            <h1 className="text-display-responsive text-primary leading-tight">
              Quran Tafseer Program
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl">
              Unlock the deep meanings, wisdoms, and historical contexts of the Quranic verses. Apply the timeless wisdom of the Quran to your daily life under the guidance of qualified Islamic scholars.
            </p>
            <div className="flex flex-col xs:flex-row flex-wrap gap-3 sm:gap-4 pt-4">
              <Link
                to="/contact"
                className="inline-block bg-primary text-on-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full w-full sm:w-auto text-center font-headline-md text-headline-md shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
          <div className="flex-1 relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 md:border-8 border-white">
              <img
                alt="Open Holy Quran showing study notes"
                className="w-full h-auto object-cover"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuDvgTtynn3TFYA71yNEoUEhkFp2MMLVoeMvUHQSBxFsdMv55iaqwdyAie0VRisR7OCB2E4uOXBFeBYApH0BNsKoVdiftepkl3xfE87XZQyvO-OypzB5T9h6hBkIBz-Lejl5qw9NO0HechCVvTw2MC4GOS10_WehLF-n-AtX8kJRjDPV2eVrbzREMgDf4JacISYGRgy0JbCJWWhLV-o_POPgud7Y80F5PImDZOw0IrvxCk2CaEbKu3Fx8CGpno3cmDw26EWp0FIger1K"
              />
            </div>
            <div className="relative md:absolute md:-bottom-8 md:-left-8 mt-6 md:mt-0 bg-white p-4 sm:p-6 rounded-2xl shadow-xl border border-outline-variant flex items-center gap-4 max-w-[calc(100vw-2rem)] md:max-w-none">
              <div className="bg-tertiary-container p-3 rounded-full">
                <Icon
                  name="menu_book"
                  className="material-symbols-outlined text-on-tertiary-container"
                  filled
                />
              </div>
              <div>
                <p className="font-label-md text-label-md text-on-surface-variant">
                  Comprehension
                </p>
                <p className="font-headline-md text-headline-md text-primary">
                  Practical Wisdom
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-white">
        <div className="page-x max-w-max-width mx-auto">
          <div className="text-center mb-10 md:mb-16 space-y-4">
            <h2 className="text-headline-responsive text-primary">
              Expected Outcomes
            </h2>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-2xl mx-auto">
              Our Tafseer curriculum is designed to help you build a deep relationship with Allah's words through translation, context, and modern application.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-gutter">
            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="translate"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Verse Translation
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Master the literal and contextual translation of Quranic Arabic words to comprehend meanings directly.
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="history_edu"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Asbab al-Nuzul
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Understand the historical background and events surrounding the revelation of specific Surahs and verses.
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="psychology"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Thematic Insights
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Learn the core values, moral rules, theological concepts, and spiritual commands inside each section of the Quran.
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="psychology_alt"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Modern Application
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Synthesize the stories and rulings of the Quran to answer modern challenges and direct daily actions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-surface-container-low">
        <div className="page-x max-w-max-width mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
          <div className="flex-1 space-y-8">
            <h2 className="text-headline-responsive text-primary">
              Who is this program for?
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-full bg-tertiary-fixed flex items-center justify-center">
                  <Icon
                    name="done"
                    className="material-symbols-outlined text-on-tertiary-fixed"
                  />
                </div>
                <div>
                  <h4 className="font-headline-md text-headline-md text-on-surface">
                    Seekers of Knowledge
                  </h4>
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    Individuals who want to move beyond simple recitation to understand the deeper meanings and commands of Allah's book.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-full bg-primary-fixed flex items-center justify-center">
                  <Icon
                    name="done"
                    className="material-symbols-outlined text-on-primary-fixed"
                  />
                </div>
                <div>
                  <h4 className="font-headline-md text-headline-md text-on-surface">
                    Hafiz Graduates &amp; Students
                  </h4>
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    Those memorizing or who have memorized the Quran, since understanding the meaning greatly enhances retention and connection.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-white p-8 rounded-3xl border border-outline-variant shadow-lg space-y-6">
            <h3 className="font-headline-md text-primary text-center">Tafseer Curriculum</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Classical Tafseer Sources</span>
                  <p className="font-body-md text-on-surface-variant">References from verified classical authorities (e.g. Tafseer Ibn Kathir).</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Arabic Grammar Basics</span>
                  <p className="font-body-md text-on-surface-variant">Uncovering word derivations and grammatical nuances to enrich meaning.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Interactive Discussions</span>
                  <p className="font-body-md text-on-surface-variant">Q&amp;A sessions to clarify doubts and apply lessons to personal growth.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section-y page-x max-w-max-width mx-auto">
        <div className="gradient-mesh rounded-section p-8 md:p-16 flex flex-col md:flex-row items-center justify-between border border-primary-fixed/30 gap-8">
          <div className="max-w-xl">
            <h2 className="text-display-responsive text-primary mb-4 font-bold">
              Ready to understand the Quran deeply?
            </h2>
            <p className="text-body-lg text-on-surface-variant">
              Register today and start your journey of intellectual and spiritual transformation under our authorized teachers.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
            <Link
              to="/contact"
              className="bg-primary text-on-primary px-8 py-4 rounded-full text-center font-bold shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
            >
              Book Free Trial
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
